// /player-economy-metrics — player economy dashboard (sendcash / bail / referrals / donator spends).
// Epic #550 Phase 1, issue #565.
//
// Scope note: Aggregate flows only; per-counterparty drill-down deferred (PII).
// The API returns daily sums — it does NOT expose cx_from/cx_to pairs,
// individual bail payers, or any other per-user counterparty data.
// That would require the v1-B PII-access audit pipeline which is out of scope.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { EconomyDashboardLayout } from '@/components/admin/economy-dashboard-layout';

// ── Types ─────────────────────────────────────────────────────────────────────

interface CashTransferDayRow {
  day: string;
  total_amount: number;
  count: number;
}

interface BailDayRow {
  day: string;
  total_bail: number;
  total_minutes: number;
  count: number;
}

interface ReferralDayRow {
  day: string;
  total_credits_earned: number;
  total_points_used: number;
  count: number;
}

interface DonatorTypeRow {
  type: string;
  total_credits: number;
}

interface PlayerEconomyResponse {
  ok: boolean;
  range: { since: number | null; until: number | null };
  cash_transfers: CashTransferDayRow[];
  bail: BailDayRow[];
  referral_credits: ReferralDayRow[];
  donator_by_type: DonatorTypeRow[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Format money per D-Currency-1: cash = ¥ (U+00A5), credits = ₢ (U+20A2). */
function formatMoney(value: number, currency: 'cash' | 'credits'): string {
  const symbol = currency === 'credits' ? '₢' : '¥';
  return `${symbol}${value.toLocaleString()}`;
}

function buildQueryString(since: string, until: string, userId: string): string {
  const params = new URLSearchParams();
  if (since.trim()) params.set('since', since.trim());
  if (until.trim()) params.set('until', until.trim());
  if (userId.trim()) params.set('userId', userId.trim());
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

/** Classify a donator type string as grant (source) or spend (sink). */
function donatorTypeLabel(type: string): string {
  if (type === 'paypal_grant') return `${type} — grant (source)`;
  return `${type} — spend (sink)`;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function PlayerEconomyMetricsPage() {
  const [draftSince, setDraftSince] = useState('');
  const [draftUntil, setDraftUntil] = useState('');
  const [draftUserId, setDraftUserId] = useState('');
  const [appliedSince, setAppliedSince] = useState('');
  const [appliedUntil, setAppliedUntil] = useState('');
  const [appliedUserId, setAppliedUserId] = useState('');

  const { data, isLoading, isError } = useQuery<PlayerEconomyResponse>({
    queryKey: ['player-economy-metrics', appliedSince, appliedUntil, appliedUserId],
    queryFn: async () => {
      const qs = buildQueryString(appliedSince, appliedUntil, appliedUserId);
      const res = await apiFetch<PlayerEconomyResponse>(`/admin/player-economy-metrics${qs}`);
      if (!res.ok) throw new Error('Failed to load player economy metrics.');
      return res.data;
    },
  });

  function handleApply() {
    setAppliedSince(draftSince);
    setAppliedUntil(draftUntil);
    setAppliedUserId(draftUserId);
  }

  return (
    <EconomyDashboardLayout
      title="Player Economy"
      description="Aggregate player economy flows: P2P cash transfers, jail bail, referral credit injections, and donator credit activity. Defaults to all-time; narrow by Unix timestamp range."
      since={draftSince}
      until={draftUntil}
      onSinceChange={setDraftSince}
      onUntilChange={setDraftUntil}
      userId={draftUserId}
      onUserIdChange={setDraftUserId}
      onApply={handleApply}
      isLoading={isLoading}
      isError={isError}
      errorMessage="Failed to load player economy metrics."
    >
      <p className="text-sm text-ink-mid italic">
        Aggregate flows only; per-counterparty drill-down deferred (PII).
      </p>

      {data != null && (
        <>
          {/* P2P Cash Transfers */}
          <section className="space-y-2">
            <h2 className="text-lg font-medium text-ink-high">P2P Cash Transfers</h2>
            {data.cash_transfers.length > 0 ? (
              <div className="overflow-x-auto rounded-sm border border-border">
                <table className="w-full text-sm" data-testid="player-economy-cash-transfers-table">
                  <thead className="bg-muted text-left">
                    <tr>
                      <th className="px-3 py-2 font-medium text-ink-mid">Day</th>
                      <th className="px-3 py-2 font-medium text-ink-mid">Total Amount</th>
                      <th className="px-3 py-2 font-medium text-ink-mid">Count</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-divider">
                    {data.cash_transfers.map((row) => (
                      <tr key={row.day} className="hover:bg-panel-elev">
                        <td className="px-3 py-2 font-mono text-xs">{row.day}</td>
                        <td className="px-3 py-2 font-mono text-xs tabular-nums">{formatMoney(row.total_amount, 'cash')}</td>
                        <td className="px-3 py-2 tabular-nums">{row.count.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-ink-low">No cash transfer data available.</p>
            )}
          </section>

          {/* Jail Bail */}
          <section className="space-y-2">
            <h2 className="text-lg font-medium text-ink-high">Jail Bail</h2>
            {data.bail.length > 0 ? (
              <div className="overflow-x-auto rounded-sm border border-border">
                <table className="w-full text-sm" data-testid="player-economy-bail-table">
                  <thead className="bg-muted text-left">
                    <tr>
                      <th className="px-3 py-2 font-medium text-ink-mid">Day</th>
                      <th className="px-3 py-2 font-medium text-ink-mid">Total Bail</th>
                      <th className="px-3 py-2 font-medium text-ink-mid">Total Minutes</th>
                      <th className="px-3 py-2 font-medium text-ink-mid">Count</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-divider">
                    {data.bail.map((row) => (
                      <tr key={row.day} className="hover:bg-panel-elev">
                        <td className="px-3 py-2 font-mono text-xs">{row.day}</td>
                        <td className="px-3 py-2 font-mono text-xs tabular-nums">{formatMoney(row.total_bail, 'cash')}</td>
                        <td className="px-3 py-2 tabular-nums">{row.total_minutes.toLocaleString()}</td>
                        <td className="px-3 py-2 tabular-nums">{row.count.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-ink-low">No bail data available.</p>
            )}
          </section>

          {/* Referral Credit Injections */}
          <section className="space-y-2">
            <h2 className="text-lg font-medium text-ink-high">Referral Credit Injections</h2>
            {data.referral_credits.length > 0 ? (
              <div className="overflow-x-auto rounded-sm border border-border">
                <table className="w-full text-sm" data-testid="player-economy-referral-credits-table">
                  <thead className="bg-muted text-left">
                    <tr>
                      <th className="px-3 py-2 font-medium text-ink-mid">Day</th>
                      <th className="px-3 py-2 font-medium text-ink-mid">Credits Injected</th>
                      <th className="px-3 py-2 font-medium text-ink-mid">Points Used</th>
                      <th className="px-3 py-2 font-medium text-ink-mid">Count</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-divider">
                    {data.referral_credits.map((row) => (
                      <tr key={row.day} className="hover:bg-panel-elev">
                        <td className="px-3 py-2 font-mono text-xs">{row.day}</td>
                        <td className="px-3 py-2 font-mono text-xs tabular-nums">{formatMoney(row.total_credits_earned, 'credits')}</td>
                        <td className="px-3 py-2 tabular-nums">{row.total_points_used.toLocaleString()}</td>
                        <td className="px-3 py-2 tabular-nums">{row.count.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-ink-low">No referral credit injection data available.</p>
            )}
          </section>

          {/* Donator Credits by Type */}
          <section className="space-y-2">
            <h2 className="text-lg font-medium text-ink-high">Donator Credits by Type</h2>
            <p className="text-sm text-ink-mid">
              Rows are not netted. <code className="font-mono text-xs">paypal_grant</code> is a credit source
              (grant); all other types are credit sinks (spends). Review per-type to understand economy
              inflows vs. outflows.
            </p>
            {data.donator_by_type.length > 0 ? (
              <div className="overflow-x-auto rounded-sm border border-border">
                <table className="w-full text-sm" data-testid="player-economy-donator-by-type-table">
                  <thead className="bg-muted text-left">
                    <tr>
                      <th className="px-3 py-2 font-medium text-ink-mid">Type</th>
                      <th className="px-3 py-2 font-medium text-ink-mid">Total Credits</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-divider">
                    {data.donator_by_type.map((row) => (
                      <tr key={row.type} className="hover:bg-panel-elev">
                        <td className="px-3 py-2 font-mono text-xs">{donatorTypeLabel(row.type)}</td>
                        <td className="px-3 py-2 font-mono text-xs tabular-nums">{formatMoney(row.total_credits, 'credits')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-ink-low">No donator credit data available.</p>
            )}
          </section>
        </>
      )}
    </EconomyDashboardLayout>
  );
}
